import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-lazy-hydration attribute.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') return;

  await sectionRenderer.renderSection(normalizedId, { cache: false, url });
  section.dataset.hydrated = 'true';
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url = new URL(window.location.href)) {
  onDocumentReady(() => {
    requestIdleCallback(() => hydrateSection(sectionId, url));
  });
}

/**
 * Re-run inline and external scripts inside a container.
 * This ensures all <script> in replaced DOM are re-executed.
 */
export function reloadScripts(container) {
  container.querySelectorAll('script').forEach((oldScript) => {
    const newScript = document.createElement('script');
    for (const attr of oldScript.attributes) {
      newScript.setAttribute(attr.name, attr.value);
    }
    newScript.textContent = oldScript.textContent;
    oldScript.replaceWith(newScript);
  });
}

/**
 * Rehydrate all sections and re-init highlight after page transition completes.
 */
window.addEventListener('page-transition:done', () => {
  import('@theme/critical').then(({ updateAllHeaderCustomProperties }) => updateAllHeaderCustomProperties());
  // Optional: re-init custom scripts (e.g. highlight.js)
  import('@theme/code-highlight-init').then(({ initHighlight }) => initHighlight());
});